"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
export default function OurRootsInTexas() {
  const headlineReveal = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.3
  });
  const passageReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const imageReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const captionReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const disciplines = [{
    label: "01",
    name: "Integrity",
    body: "We approach every canopy with surgical intent. Our arborists don't just cut; they diagnose. We prioritize the long-term structural health of your trees over quick, aesthetic fixes, ensuring your DFW landscape thrives for generations to come."
  }, {
    label: "02",
    name: "Precision",
    body: "Safety is our baseline; precision is our signature. Utilizing advanced rigging and modern climbing techniques, we execute complex removals and delicate pruning with zero impact on your property. Every cut is calculated to the millimeter."
  }, {
    label: "03",
    name: "Legacy",
    body: "Arbor Pro is built on a reputation of Texan reliability. We treat every residential project as a contribution to the local ecosystem. From the first assessment to the final cleanup, we honor the trust homeowners place in our hands."
  }];
  return <section data-section-id="4016" id="our-roots-in-texas" className="bg-background text-foreground">
      <h1 className="sr-only">Our Story: 25+ Years of DFW Tree Service Excellence</h1>
      {}
      <div className="px-6 md:px-12 pt-32 md:pt-60 pb-24 md:pb-40">
        <div className="mx-auto max-w-[1400px] grid grid-cols-12 gap-4 md:gap-6">
          <div className="col-span-12 md:col-start-2 md:col-end-12 flex flex-col items-center text-center">
            <div className="font-mono text-[11px] md:text-xs tracking-[0.2em] uppercase mb-12 md:mb-20 transition-all duration-1000 ease-out" style={{
            color: "var(--accent)",
            opacity: headlineReveal.isVisible ? 1 : 0,
            transform: headlineReveal.isVisible ? "translateY(0)" : "translateY(12px)"
          }}>
              01 // OUR ROOTS
            </div>

            <h2 ref={headlineReveal.ref} className="font-default font-extrabold leading-[0.95] tracking-tighter text-[64px] sm:text-[96px] md:text-[128px] lg:text-[160px] uppercase" aria-label="Texas grown, expert care.">
              <span className="block overflow-hidden">
                {"Texas grown,".split("").map((char, i) => <span key={`l1-${i}`} data-index={i} className="inline-block transition-all duration-700 ease-out" style={{
                opacity: headlineReveal.isVisible ? 1 : 0,
                transform: headlineReveal.isVisible ? "translateY(0)" : "translateY(60%)",
                transitionDelay: `${i * 40}ms`,
                color: char === "," ? "var(--accent)" : "inherit"
              }}>
                    {char === " " ? "\u00A0" : char}
                  </span>)}
              </span>
              <span className="block overflow-hidden">
                {"expert care.".split("").map((char, i) => <span key={`l2-${i}`} data-index={i} className="inline-block transition-all duration-700 ease-out" style={{
                opacity: headlineReveal.isVisible ? 1 : 0,
                transform: headlineReveal.isVisible ? "translateY(0)" : "translateY(60%)",
                transitionDelay: `${(i + 10) * 40}ms`,
                color: char === "." ? "var(--accent)" : "inherit"
              }}>
                    {char === " " ? "\u00A0" : char}
                  </span>)}
              </span>
            </h2>
          </div>

          <div ref={passageReveal.ref} className="col-span-12 md:col-start-4 md:col-end-10 mt-20 md:mt-32 transition-all duration-1000 ease-out" style={{
          opacity: passageReveal.isVisible ? 1 : 0,
          transform: passageReveal.isVisible ? "translateY(0)" : "translateY(24px)"
        }}>
            <p className="font-default font-normal text-xl md:text-2xl leading-[1.6] text-foreground/90">
              Arbor Pro was founded on a singular commitment: to bring <Link to="/OurExpertisePage" className="underline underline-offset-4 decoration-accent hover:text-accent transition-colors">expert arboricultural care</Link> to the 
              DFW landscape. We are not just tree cutters; we are stewards of the urban forest. 
              Our team of certified arborists operates with the discipline of engineers, 
              stripping away the noise of traditional landscaping to focus on the structural 
              integrity and biological health of your trees. Whether we are performing a 
              surgical prune on a century-old oak or managing a complex removal near power lines, 
              our work is defined by technical mastery and a deep respect for the Texan landscape. 
              We believe that professional <Link to="/ResidentialTreeCarePage" className="underline underline-offset-4 decoration-accent hover:text-accent transition-colors">residential tree care</Link> and specialized <Link to="/HoaTreeCarePage" className="underline underline-offset-4 decoration-accent hover:text-accent transition-colors">HOA tree care</Link> are an investment in the safety and value 
              of your home—one that requires patience, expertise, and the right tools for the job. 
              This is the standard we bring to every property in Fort Worth, <Link to="/CommunitiesWeServePage" className="underline underline-offset-4 decoration-accent hover:text-accent transition-colors">surrounding DFW communities</Link>, and <Link to="/GetQuotePage" className="underline underline-offset-4 decoration-accent hover:text-accent transition-colors">beyond</Link>.
            </p>
          </div>
        </div>
      </div>

      {}
      <div ref={imageReveal.ref} className="relative w-full h-screen overflow-hidden">
        <div className="absolute inset-0 transition-all ease-out" style={{
        clipPath: imageReveal.isVisible ? "inset(0% 0 0 0)" : "inset(100% 0 0 0)",
        transitionDuration: "1800ms"
      }}>
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/IMG_5410.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/IMG_5410.jpg-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/IMG_5410.jpg-wvc-sizes" alt="Arbor Pro specialist performing precision pruning in a DFW canopy" loading="lazy" decoding="async" className="w-full h-full object-cover grayscale contrast-125" />
          <div className="absolute inset-0" style={{
          background: "linear-gradient(to top, hsla(165, 25%, 6%, 0.8) 0%, hsla(165, 25%, 6%, 0) 40%)"
        }} />
        </div>

        <div ref={captionReveal.ref} className="absolute bottom-8 md:bottom-12 left-6 md:left-12 transition-all duration-1000 ease-out" style={{
        opacity: captionReveal.isVisible ? 1 : 0,
        transform: captionReveal.isVisible ? "translateY(0)" : "translateY(12px)",
        transitionDelay: "800ms"
      }}>
          <div className="font-mono text-[10px] md:text-xs tracking-[0.2em] uppercase text-white/80">
            ISA CERTIFIED · FULLY INSURED · DFW CANOPY SPECIALISTS
          </div>
        </div>
      </div>

      {}
      <div className="px-6 md:px-12 py-32 md:py-48" style={{
      backgroundColor: "var(--forest-obsidian)",
      color: "var(--white)"
    }}>
        <div className="mx-auto max-w-[1400px]">
          <div className="mb-20 md:mb-28">
            <div className="font-mono text-[11px] md:text-xs tracking-[0.2em] uppercase" style={{
            color: "var(--accent)"
          }}>
              02 // THE DISCIPLINES
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
            {disciplines.map((d, i) => <DisciplineColumn key={d.name} data-index={i} index={i} label={d.label} name={d.name} body={d.body} showDivider={i < disciplines.length - 1} />)}
          </div>
        </div>
      </div>
    </section>;
}
function DisciplineColumn({
  index,
  label,
  name,
  body,
  showDivider,
  ...rest
}: {
  index: number;
  label: string;
  name: string;
  body: string;
  showDivider: boolean;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <div ref={ref} {...rest} className="relative px-0 md:px-8 py-12 md:py-0 transition-all duration-1000 ease-out" style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(24px)",
    transitionDelay: `${index * 180}ms`
  }}>
      <div className="font-mono text-[11px] tracking-[0.2em] uppercase mb-6" style={{
      color: "var(--accent)"
    }}>
        {label} — METHOD
      </div>

      <h3 className="font-default font-bold text-[32px] md:text-[36px] leading-tight mb-8 uppercase tracking-tight" style={{
      color: "var(--white)"
    }}>
        {name}
      </h3>

      <p className="font-default text-base md:text-lg leading-[1.7] font-normal" style={{
      color: "var(--muted-foreground)"
    }}>
        {body}
      </p>

      {showDivider && <div className="hidden md:block absolute top-0 right-0 h-full">
          <Separator orientation="vertical" className="h-full" style={{
        backgroundColor: "var(--timber-gray)",
        opacity: 0.2
      }} />
        </div>}

      {showDivider && <div className="md:hidden mt-12">
          <Separator orientation="horizontal" style={{
        backgroundColor: "var(--timber-gray)",
        opacity: 0.2
      }} />
        </div>}
    </div>;
}